import { SupportedLocale, getCareBriefCreateURL } from '@99designs/i18n';
import { DesignProduct, ProductKey, VistaDesignProduct } from './types';

const buildVistaDesignProduct = (
    mpvId: ProductKey,
    title: string,
    availableLocales: SupportedLocale[]
): VistaDesignProduct => {
    return {
        type: 'vista',
        key: mpvId,
        mpvId: mpvId,
        title: title,
        availableLocales: availableLocales,
        urls: {
            getStarted: `/design/get-started?mpvId=${mpvId}&locale={locale}`,
            brief: getCareBriefCreateURL(mpvId),
        },
        images: {
            preview: {
                type: 'cloudinary',
                cloudinaryImagePath: `PreviewImages/${mpvId}`,
                aspectRatio: 0.75,
            },
            searchPreview: {
                type: 'cloudinary',
                cloudinaryImagePath: `/expert_services/HubPage/Search%20Preview%20Images/${mpvId}/${mpvId}-EN-US.png`,
                aspectRatio: 1,
            },
        },
    };
};

export const designProducts: Record<ProductKey, DesignProduct> = {
    'brand-identity-pack': {
        type: 'nn',
        key: 'brand-identity-pack',
        productId: 'brand-identity-pack',
        title: 'Logo & Brand Identity Pack Design',
        availableLocales: ['en-US'],
        urls: {
            nnCategoryPage: '/brand-identity-pack/details',
        },
        images: {
            preview: {
                type: 'cloudinary',
                cloudinaryImagePath: `/expert_services/HubPage/Preview%20Images/brand-identity-pack-EN-US.png`,
                aspectRatio: 0.75,
            },
            searchPreview: {
                type: 'cloudinary',
                cloudinaryImagePath: `/expert_services/HubPage/Search%20Preview%20Images/brand-identity-pack-EN-US.png`,
                aspectRatio: 1,
            },
        },
    },
    logoEssential: {
        type: 'bifrost',
        key: 'logoEssential',
        mpvId: 'logoEssential',
        title: 'Essential Logo Package',
        availableLocales: [
            'en-US',
            'es-US',
            'en-CA',
            'fr-CA',
            'en-AU',
            'en-NZ',
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-GB',
            'en-IE',
            'en-SG',
            'es-ES',
            'fi-FI',
            'fr-BE',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ],
        urls: {
            nnCategoryPage: '/logo-design',
            productDetails: '/design/logo-essential',
        },
        images: {
            preview: {
                type: 'cloudinary',
                cloudinaryImagePath: `/expert_services/HubPage/Preview%20Images/logoEssential/logoEssential-EN-US.png`,
                aspectRatio: 0.75,
            },
            searchPreview: {
                type: 'cloudinary',
                cloudinaryImagePath: `/expert_services/HubPage/Search%20Preview%20Images/logoEssential/logoEssential-EN-US.png`,
                aspectRatio: 1,
            },
        },
    },
    acrylicWeddingSigns: buildVistaDesignProduct(
        'acrylicWeddingSigns',
        'Acrylic Wedding Signs Design',
        ['en-CA', 'fr-CA']
    ),
    basicKidsShortSleeveTShirts: buildVistaDesignProduct(
        'basicKidsShortSleeveTShirts',
        "Fruit of the Loom® Kids' T-shirt Design",
        ['en-US', 'es-US']
    ),
    bellaCanvasUnisexShortSleeveJerseyTShirtDtg2go: buildVistaDesignProduct(
        'bellaCanvasUnisexShortSleeveJerseyTShirtDtg2go',
        'Bella + Canvas® Ultra-Soft Jersey Unisex T-shirt',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    caBasicTShirts: buildVistaDesignProduct(
        'caBasicTShirts',
        'Fruit of the Loom® Unisex T-shirt Design',
        ['en-US', 'es-US']
    ),
    carbonlessForms: buildVistaDesignProduct('carbonlessForms', 'Carbonless Forms Design', [
        'en-CA',
        'fr-CA',
    ]),
    carDoorMagnets: buildVistaDesignProduct('carDoorMagnets', 'Custom Car Magnets', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    carharttMidweightHoodedSweatshirtNa: buildVistaDesignProduct(
        'carharttMidweightHoodedSweatshirtNa',
        'Carhartt® Loose-Fit Midweight Hoodie Design',
        ['en-US', 'es-US']
    ),
    circleSelfInkingStamps: buildVistaDesignProduct(
        'circleSelfInkingStamps',
        'Circular Self-Inking Stamps Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    circleStickers: buildVistaDesignProduct('circleStickers', 'Circle Sticker Design', [
        'da-DK',
        'de-CH',
        'de-DE',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    classicCottonToteBagSmall: buildVistaDesignProduct(
        'classicCottonToteBagSmall',
        'VistaPrint® Cotton Tote Bag Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    customSheetStickers: buildVistaDesignProduct('customSheetStickers', 'Sheet Stickers Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    customSizeVinylBanners: buildVistaDesignProduct(
        'customSizeVinylBanners',
        'Custom Size Vinyl Banners Design',
        ['en-US', 'es-US']
    ),
    customTissuePaper: buildVistaDesignProduct('customTissuePaper', 'Custom Tissue Paper Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    deluxeCottonTote: buildVistaDesignProduct(
        'deluxeCottonTote',
        'VistaPrint® Large Cotton Tote Bag Design',
        ['en-CA', 'en-IE', 'fr-CA']
    ),
    displayMailers: buildVistaDesignProduct('displayMailers', 'Mailer Boxes Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    doorHangers: buildVistaDesignProduct('doorHangers', 'Door Hangers Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    doubleSidedBanners: buildVistaDesignProduct(
        'doubleSidedBanners',
        'Double-Sided Vinyl Banners Design',
        ['en-US', 'es-US']
    ),
    embossedGlossBusinessCards: buildVistaDesignProduct(
        'embossedGlossBusinessCards',
        'Embossed Gloss Business Cards Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    fabricBanners: buildVistaDesignProduct('fabricBanners', 'Fabric Banners Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    flags: buildVistaDesignProduct('flags', 'Flags Design', [
        'de-CH',
        'en-AU',
        'en-CA',
        'en-US',
        'es-ES',
        'es-US',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'nl-BE',
    ]),
    flatMenus: buildVistaDesignProduct('flatMenus', 'Flat Menus', ['es-US']),
    flatPouches: buildVistaDesignProduct('flatPouches', 'Flat Pouches Design', [
        'en-US',
        'es-US',
        'nl-NL',
    ]),
    flyers: buildVistaDesignProduct('flyers', 'Flyers Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    foamBoardWeddingSigns: buildVistaDesignProduct(
        'foamBoardWeddingSigns',
        'Foam Board Wedding Signs Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    foamBoards: buildVistaDesignProduct('foamBoards', 'Foam Board Design', [
        'da-DK',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
        'fr-CA',
        'nl-NL',
        'sv-SE',
    ]),
    foilAccentBusinessCards: buildVistaDesignProduct(
        'foilAccentBusinessCards',
        'Foil Accent Business Cards Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    foodPackagingLabels: buildVistaDesignProduct(
        'foodPackagingLabels',
        'Food Packaging Labels Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    giftCertificate: buildVistaDesignProduct('giftCertificate', 'Gift Certificates Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    gildanHeavyCottonYouthTShirtNa: buildVistaDesignProduct(
        'gildanHeavyCottonYouthTShirtNa',
        'Gildan® Heavy Cotton™ Youth T-Shirt Design',
        ['en-CA', 'fr-CA']
    ),
    gildanZipUpSweatshirt: buildVistaDesignProduct(
        'gildanZipUpSweatshirt',
        'JERZEES® Zip Hooded Sweatshirt Design',
        ['de-CH', 'de-DE', 'en-CA', 'en-GB', 'fi-FI', 'fr-CA', 'fr-FR', 'it-IT', 'nb-NO']
    ),
    glossyBusinessCards: buildVistaDesignProduct(
        'glossyBusinessCards',
        'Glossy Business Cards Design',
        [
            'da-DK',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'sv-SE',
        ]
    ),
    hanesAuthenticYouthShortSleeveTShirtNa: buildVistaDesignProduct(
        'hanesAuthenticYouthShortSleeveTShirtNa',
        'Hanes®Authentic Youth Short Sleeve T-Shirt Design',
        ['es-US']
    ),
    hangTags: buildVistaDesignProduct('hangTags', 'Hang Tags Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    jerzeesPolo: buildVistaDesignProduct('jerzeesPolo', 'JERZEES® Piqué Polo Shirt Design', [
        'en-US',
        'es-US',
    ]),
    letterhead: buildVistaDesignProduct('letterhead', 'Letterheads Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    loyaltyCardStamps: buildVistaDesignProduct('loyaltyCardStamps', 'Loyalty Card Stamps Design', [
        'en-AU',
        'en-CA',
        'fr-CA',
    ]),
    magneticCalendars: buildVistaDesignProduct('magneticCalendars', 'Magnetic Calendars Design', [
        'en-CA',
        'fr-CA',
    ]),
    mailingServicesPostcard: buildVistaDesignProduct(
        'mailingServicesPostcard',
        'Postcard Mailing Services Design',
        ['en-US', 'es-US']
    ),
    matteBusinessCards: buildVistaDesignProduct(
        'matteBusinessCards',
        'Matte Business Cards Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    menus: buildVistaDesignProduct('menus', 'Menus Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    meshBanners: buildVistaDesignProduct('meshBanners', 'Mesh Banners Design', [
        'en-AU',
        'en-CA',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    naWeddingCelebrationInvitations: buildVistaDesignProduct(
        'naWeddingCelebrationInvitations',
        'Wedding Party Invitations Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    naWrappingPaper: buildVistaDesignProduct('naWrappingPaper', 'Custom Wrapping Paper Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    notebooks: buildVistaDesignProduct('notebooks', 'Notebooks Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    notepads: buildVistaDesignProduct('notepads', 'Notepads Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    paperBags: buildVistaDesignProduct('paperBags', 'Standard Kraft Paper Bags Design', [
        'en-US',
        'es-US',
    ]),
    payPalCarDoorMagnetsNa: buildVistaDesignProduct(
        'payPalCarDoorMagnetsNa',
        'Car Magnets Design',
        ['en-US']
    ),
    pennantBanners: buildVistaDesignProduct('pennantBanners', 'Pennant Banners Design', [
        'en-CA',
        'fr-CA',
    ]),
    plasticLabels: buildVistaDesignProduct('plasticLabels', 'Waterproof Labels Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    poleBanners: buildVistaDesignProduct('poleBanners', 'Pole Banners Design', ['en-CA', 'fr-CA']),
    polyMailers: buildVistaDesignProduct('polyMailers', 'Poly Mailers Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    portAuthorityLifestyleBrimHatNa: buildVistaDesignProduct(
        'portAuthorityLifestyleBrimHatNa',
        'Port Authority® Lifestyle Brim Hat Design',
        ['en-CA', 'fr-CA']
    ),
    postcards: buildVistaDesignProduct('postcards', 'Standard Postcards Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    posters: buildVistaDesignProduct('posters', 'Posters Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    preInkedStamps: buildVistaDesignProduct('preInkedStamps', 'Pre-Inked Stamps Design', [
        'en-US',
        'es-US',
    ]),
    presentationFolders: buildVistaDesignProduct(
        'presentationFolders',
        'Presentation Folders Design',
        [
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
        ]
    ),
    premiumPlusBusinessCards: buildVistaDesignProduct(
        'premiumPlusBusinessCards',
        'Premium Plus Business Cards Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    productBoxes: buildVistaDesignProduct('productBoxes', 'Product Boxes Design', [
        'en-AU',
        'en-CA',
        'en-NZ',
        'en-US',
        'es-US',
        'fr-CA',
        'en-US',
        'es-US',
        'en-CA',
        'fr-CA',
        'en-AU',
        'en-NZ',
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    productLabels: buildVistaDesignProduct('productLabels', 'Product Labels on Sheet Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    rackCards: buildVistaDesignProduct('rackCards', 'Rack Cards Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    raisedFoilBusinessCards: buildVistaDesignProduct(
        'raisedFoilBusinessCards',
        'Raised Foil Business Cards Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    rectangleStickers: buildVistaDesignProduct('rectangleStickers', 'Rectangle Sticker Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    retractableBanners: buildVistaDesignProduct(
        'retractableBanners',
        'Retractable Banners Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    returnAddressLabels: buildVistaDesignProduct(
        'returnAddressLabels',
        'Return Address Labels Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    rollLabels: buildVistaDesignProduct('rollLabels', 'Roll Labels Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    roundedCornerBusinessCards: buildVistaDesignProduct(
        'roundedCornerBusinessCards',
        'Rounded Corner Business Cards Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    selfInkingStamps: buildVistaDesignProduct(
        'selfInkingStamps',
        'Rectangular Self-Inking Stamps Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    shippingBoxes: buildVistaDesignProduct('shippingBoxes', 'Shipping Boxes Design', [
        'en-CA',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
        'fi-FI',
        'fr-CA',
        'sv-SE',
    ]),
    shippingEnvelopes: buildVistaDesignProduct('shippingEnvelopes', 'Shipping Envelopes Design', [
        'en-AU',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
    ]),
    standardBusinessCards: buildVistaDesignProduct(
        'standardBusinessCards',
        'Standard Business Cards Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    standardEmbroideredHats: buildVistaDesignProduct(
        'standardEmbroideredHats',
        'VistaPrint® Embroidered Cap Design',
        [
            'da-DK',
            'de-CH',
            'de-DE',
            'en-CA',
            'en-US',
            'es-ES',
            'es-US',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-IT',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    standUpPouches: buildVistaDesignProduct('standUpPouches', 'Stand-Up Pouches Design', [
        'en-AU',
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    stepAndRepeatBanners: buildVistaDesignProduct(
        'stepAndRepeatBanners',
        'Step & Repeat Banners Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    stickyNotebookNa: buildVistaDesignProduct('stickyNotebookNa', 'Sticky Notebook Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    stickerSingles: buildVistaDesignProduct('stickerSingles', 'Sticker Singles Design', [
        'da-DK',
        'de-AT',
        'de-DE',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    tablecloths: buildVistaDesignProduct('tablecloths', 'Custom Tablecloths Design', [
        'en-NZ',
        'en-US',
        'es-US',
    ]),
    tuckTopboxes: buildVistaDesignProduct('tuckTopboxes', 'Tuck Top Boxes Design', [
        'en-US',
        'es-US',
    ]),
    ultraCottonYouthTShirt: buildVistaDesignProduct(
        'ultraCottonYouthTShirt',
        'Gildan® Ultra Cotton® Kids’ T-shirt',
        ['en-US', 'es-US']
    ),
    vinylBanners: buildVistaDesignProduct('vinylBanners', 'Vinyl Banners Design', [
        'en-AU',
        'en-CA',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    vistaPrintPrintedBaseballCap: buildVistaDesignProduct(
        'vistaPrintPrintedBaseballCap',
        'VistaPrint® Printed Baseball Cap Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-CA',
            'en-IE',
            'en-NZ',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    vistaprintCuffedBeanie: buildVistaDesignProduct(
        'vistaprintCuffedBeanie',
        'VistaPrint® Cuffed Beanie Design',
        ['de-AT', 'de-DE', 'en-NZ', 'en-US', 'es-US']
    ),
    vistaprintPremiumLaptopBackpack15: buildVistaDesignProduct(
        'vistaprintPremiumLaptopBackpack15',
        'VistaPrint® Premium Laptop Backpack 15" Design',
        ['en-US', 'es-US']
    ),
    weddingEnclosureCards: buildVistaDesignProduct(
        'weddingEnclosureCards',
        'Wedding Enclosure Cards Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    weddingInvitations: buildVistaDesignProduct(
        'weddingInvitations',
        'Wedding Invitations Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-CA',
            'en-GB',
            'en-IE',
            'en-NZ',
            'en-SG',
            'en-US',
            'es-ES',
            'es-US',
            'fi-FI',
            'fr-BE',
            'fr-CA',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    weddingMenus: buildVistaDesignProduct('weddingMenus', 'Wedding Menus Design', [
        'en-AU',
        'en-CA',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    weddingPrograms: buildVistaDesignProduct('weddingPrograms', 'Wedding Programs Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    weddingRsvpCards: buildVistaDesignProduct('weddingRsvpCards', 'Wedding RSVP Cards Design', [
        'en-US',
        'es-US',
    ]),
    weddingThankYouCards: buildVistaDesignProduct(
        'weddingThankYouCards',
        'Wedding Thank You Cards Design',
        ['en-CA', 'en-US', 'es-US', 'fr-CA']
    ),
    windowDecals: buildVistaDesignProduct('windowDecals', 'Window Decals Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-CA',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CA',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    xBanners: buildVistaDesignProduct('xBanners', 'X-Banners Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    yardSigns: buildVistaDesignProduct('yardSigns', 'Yard Signs Design', [
        'en-CA',
        'en-US',
        'es-US',
        'fr-CA',
    ]),
    proLogoDesign: buildVistaDesignProduct('proLogoDesign', 'Pro Logo Design', ['en-US']),
    invitationsAnnouncements: buildVistaDesignProduct(
        'invitationsAnnouncements',
        'Invitations & Announcements',
        ['en-US']
    ),
    qrCodeBusinessCards: buildVistaDesignProduct('qrCodeBusinessCards', 'QR Code Business Cards', [
        'en-GB',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'it-CH',
        'nb-NO',
        'nl-NL',
    ]),
    squareBusinessCards: buildVistaDesignProduct('squareBusinessCards', 'Square Business Cards', [
        'en-NZ',
        'en-US',
        'it-CH',
        'it-IT',
        'sv-SE',
    ]),
    ultraThickBusinessCards: buildVistaDesignProduct(
        'ultraThickBusinessCards',
        'Ultra Thick Business Cards',
        ['en-US']
    ),
    plasticBusinessCards: buildVistaDesignProduct(
        'plasticBusinessCards',
        'Frosted Business Cards',
        ['en-NZ', 'en-US', 'it-CH', 'pt-PT']
    ),
    premiumMatteBusinessCards: buildVistaDesignProduct(
        'premiumMatteBusinessCards',
        'Premium Matte Business Cards',
        ['en-US']
    ),
    premiumGlossyBusinessCards: buildVistaDesignProduct(
        'premiumGlossyBusinessCards',
        'Premium Glossy Business Cards',
        ['en-US']
    ),
    uncoatedBusinessCards: buildVistaDesignProduct(
        'uncoatedBusinessCards',
        'Uncoated Business Cards',
        ['en-US']
    ),
    premiumRoundedCornerBusinessCards: buildVistaDesignProduct(
        'premiumRoundedCornerBusinessCards',
        'Premium Rounded Business Cards',
        ['en-US']
    ),
    paintedEdgeBusinessCards: buildVistaDesignProduct(
        'paintedEdgeBusinessCards',
        'Painted Edge Business Cards',
        ['en-US']
    ),
    loyaltyBusinessCards: buildVistaDesignProduct(
        'loyaltyBusinessCards',
        'Loyalty Business Cards',
        ['en-US']
    ),
    naturalTexturedBusinessCards: buildVistaDesignProduct(
        'naturalTexturedBusinessCards',
        'Natural Textured Business Cards',
        ['en-US']
    ),
    durableBusinessCards: buildVistaDesignProduct(
        'durableBusinessCards',
        'Durable Business Cards',
        ['en-US']
    ),
    premiumStandardShapeBusinessCards: buildVistaDesignProduct(
        'premiumStandardShapeBusinessCards',
        'Premium Standard Shape business cards',
        ['en-US']
    ),
    kraftBusinessCards: buildVistaDesignProduct('kraftBusinessCards', 'Kraft Business Cards', [
        'en-US',
    ]),
    bambooBusinessCards: buildVistaDesignProduct('bambooBusinessCards', 'Bamboo Business Cards', [
        'en-US',
    ]),
    hempBusinessCards: buildVistaDesignProduct('hempBusinessCards', 'Hemp-Blend Business Cards', [
        'en-US',
    ]),
    premiumSquareBusinessCards: buildVistaDesignProduct(
        'premiumSquareBusinessCards',
        'Premium Square Business Cards',
        ['en-US']
    ),
    nfcBusinessCards: buildVistaDesignProduct(
        'nfcBusinessCards',
        'NFC Business Cards with VistaConnect',
        ['en-US']
    ),
    premiumZipToteBags: buildVistaDesignProduct(
        'premiumZipToteBags',
        'VistaPrint® Large Zip Cotton Tote Bag',
        ['en-US', 'it-IT']
    ),
    bulletNatural5ozCottonCanvasGroceryToteNa: buildVistaDesignProduct(
        'bulletNatural5ozCottonCanvasGroceryToteNa',
        'Natural Cotton Canvas Shopping Tote Bag',
        ['en-US']
    ),
    groceryToteWithAntimicrobialAdditiveNa: buildVistaDesignProduct(
        'groceryToteWithAntimicrobialAdditiveNa',
        'Grocery Tote with Antimicrobial Additive',
        ['en-US']
    ),
    koozieKampKoolerToteNa: buildVistaDesignProduct(
        'koozieKampKoolerToteNa',
        'Koozie® Kamp Cooler Tote',
        ['en-US']
    ),
    iglooDaytripperToteCoolerNa: buildVistaDesignProduct(
        'iglooDaytripperToteCoolerNa',
        'Igloo® Daytripper Tote Cooler',
        ['en-US']
    ),
    rainbowRecycledCottonConventionToteNa: buildVistaDesignProduct(
        'rainbowRecycledCottonConventionToteNa',
        'Rainbow Recycled Cotton Convention Tote',
        ['en-US']
    ),
    economyToteNa: buildVistaDesignProduct('economyToteNa', 'Economy Tote', ['en-US']),
    essentialZipConventionToteNa: buildVistaDesignProduct(
        'essentialZipConventionToteNa',
        'Essential Zip Convention Tote',
        ['en-US']
    ),
    rainbowRecycledCottonGroceryToteNa: buildVistaDesignProduct(
        'rainbowRecycledCottonGroceryToteNa',
        'Rainbow Recycled Cotton Grocery Tote',
        ['en-US']
    ),
    reposeRecycledCottonBoxToteNa: buildVistaDesignProduct(
        'reposeRecycledCottonBoxToteNa',
        'Repose Recycled Cotton Box Tote',
        ['en-US']
    ),
    merchantCraftReviveRecycledToteCoolerNa: buildVistaDesignProduct(
        'merchantCraftReviveRecycledToteCoolerNa',
        'Merchant & Craft Revive Recycled Tote Cooler',
        ['en-US']
    ),
    packaginginsertcards: buildVistaDesignProduct(
        'packaginginsertcards',
        'Packaging Insert Cards',
        ['en-US', 'es-US']
    ),
    packagingTape: buildVistaDesignProduct('packagingTape', 'Water-Activated Packaging Tape', [
        'en-US',
        'es-US',
    ]),
    lockBottomBoxes: buildVistaDesignProduct('lockBottomBoxes', 'Lock Bottom Boxes', [
        'en-US',
        'es-US',
        'fr-BE',
        'fr-CH',
    ]),
    tableRunners: buildVistaDesignProduct('tableRunners', 'Table Runners', ['en-US']),
    tableTopSigns: buildVistaDesignProduct('tableTopSigns', 'Tabletop Retractable Banners', [
        'en-US',
    ]),
    metalSigns: buildVistaDesignProduct('metalSigns', 'Metal Signs', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-IE',
        'en-SG',
        'en-US',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    wallPosters: buildVistaDesignProduct('wallPosters', 'Wall Posters', ['en-US']),
    aFrames: buildVistaDesignProduct('aFrames', 'Aluminum A-Frame Signs', ['en-US']),
    acrylicSigns: buildVistaDesignProduct('acrylicSigns', 'Acrylic Signs', [
        'de-AT',
        'de-DE',
        'en-SG',
        'en-US',
        'es-ES',
    ]),
    plasticSigns: buildVistaDesignProduct('plasticSigns', 'Plastic Signs', ['en-US']),
    tableTents: buildVistaDesignProduct('tableTents', 'Table Tents', [
        'en-NZ',
        'en-SG',
        'en-US',
        'nb-NO',
    ]),
    pvcFreeBoradSigns: buildVistaDesignProduct('pvcFreeBoradSigns', 'PVC-Free Board Signs', [
        'en-US',
    ]),
    plasticDoorSigns: buildVistaDesignProduct('plasticDoorSigns', 'Plastic Door Signs', ['en-US']),
    christmasCards: buildVistaDesignProduct('christmasCards', 'Christmas Cards', ['en-US']),
    noteCards: buildVistaDesignProduct('noteCards', 'Personalized Note Cards', ['en-US']),
    thankYouCards: buildVistaDesignProduct('thankYouCards', 'Custom Thank You Cards', ['en-US']),
    customEnvelopes: buildVistaDesignProduct('customEnvelopes', 'Custom Envelopes', ['en-US']),
    '4X6Postcards': buildVistaDesignProduct('4X6Postcards', '4" x 6" Postcards', ['en-US']),
    postItNotes: buildVistaDesignProduct('postItNotes', 'Custom Post-it Notes®', ['en-US']),
    embossedFoilPostcards: buildVistaDesignProduct(
        'embossedFoilPostcards',
        'Embossed Foil Postcards',
        ['en-US']
    ),
    '5X7Postcards': buildVistaDesignProduct('5X7Postcards', '5" x 7" Postcards', ['en-US']),
    roundedCornerPostcards: buildVistaDesignProduct(
        'roundedCornerPostcards',
        'Rounded Corner Postcards',
        ['en-US']
    ),
    saveTheDateCards: buildVistaDesignProduct('saveTheDateCards', 'Save the Date Cards', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    '42X55Postcards': buildVistaDesignProduct('42X55Postcards', '4.2” x 5.5” Postcards', ['en-US']),
    '6X9Postcards': buildVistaDesignProduct('6X9Postcards', '6" x 9" Postcards', ['en-US']),
    '6X11Postcards': buildVistaDesignProduct('6X11Postcards', '6 x 11 Postcards', ['en-US']),
    businessCardStickers: buildVistaDesignProduct(
        'businessCardStickers',
        'Business Card Stickers',
        ['en-US']
    ),
    '55X85Postcards': buildVistaDesignProduct('55X85Postcards', '5.5" x 8.5" Postcards', ['en-US']),
    giftTags: buildVistaDesignProduct('giftTags', 'Gift Tags', ['en-US']),
    pocketStamps: buildVistaDesignProduct('pocketStamps', 'Pocket Stamps', [
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'it-CH',
    ]),
    embossedGlossPostcards: buildVistaDesignProduct(
        'embossedGlossPostcards',
        'Embossed Gloss Postcards',
        ['en-US']
    ),
    engravedBusinessCardHolder: buildVistaDesignProduct(
        'engravedBusinessCardHolder',
        'Engraved Business Card Holder',
        ['en-US']
    ),
    theInspirationLargeNotebookNa: buildVistaDesignProduct(
        'theInspirationLargeNotebookNa',
        'The Inspiration Large Notebook',
        ['en-US']
    ),
    neonEdgeNotebookNa: buildVistaDesignProduct('neonEdgeNotebookNa', 'Neon Edge Notebook', [
        'en-US',
    ]),
    '5X7InchSnapElasticClosureNotebookNa': buildVistaDesignProduct(
        '5X7InchSnapElasticClosureNotebookNa',
        '5 x 7-Inch Snap Elastic Closure Notebook',
        ['en-US']
    ),
    customDieCutStickerSheets: buildVistaDesignProduct(
        'customDieCutStickerSheets',
        'Die-Cut Sticker Sheets',
        ['en-US']
    ),
    bumperStickers: buildVistaDesignProduct('bumperStickers', 'Bumper Stickers', [
        'en-US',
        'pt-PT',
    ]),
    cosmeticLabels: buildVistaDesignProduct('cosmeticLabels', 'Cosmetic Labels', [
        'en-US',
        'pt-PT',
    ]),
    packagingLabels: buildVistaDesignProduct('packagingLabels', 'Packaging Labels', [
        'en-US',
        'es-US',
    ]),
    squareStickers: buildVistaDesignProduct('squareStickers', 'Square stickers', ['en-US']),
    wineLabels: buildVistaDesignProduct('wineLabels', 'Wine Labels', [
        'en-AU',
        'en-NZ',
        'en-US',
        'es-US',
        'fr-CH',
        'it-CH',
        'nb-NO',
        'pt-PT',
    ]),
    ovalStickers: buildVistaDesignProduct('ovalStickers', 'Oval Stickers', ['en-US', 'it-CH']),
    qRCodeStickers: buildVistaDesignProduct('qRCodeStickers', 'QR Code Stickers', ['en-US']),
    staticStickers: buildVistaDesignProduct('staticStickers', 'Reusable Stickers', ['en-US']),
    beerLabels: buildVistaDesignProduct('beerLabels', 'Beer Labels', ['en-US', 'es-US']),
    holographicStickers: buildVistaDesignProduct('holographicStickers', 'Holographic Stickers', [
        'en-AU',
        'en-US',
    ]),
    paperLabels: buildVistaDesignProduct('paperLabels', 'Paper Labels', ['en-US']),
    frontAdhesiveStickers: buildVistaDesignProduct(
        'frontAdhesiveStickers',
        'Front Adhesive Stickers',
        ['en-US']
    ),
    kidsLabels: buildVistaDesignProduct('kidsLabels', "Kids' School Labels", ['en-US', 'it-CH']),
    tattooStickers: buildVistaDesignProduct('tattooStickers', 'Temporary Tattoo Stickers', [
        'en-US',
    ]),
    photoStickers: buildVistaDesignProduct('photoStickers', 'Face Stickers', ['en-US']),
    partyStickers: buildVistaDesignProduct('partyStickers', 'Party Stickers', ['en-US']),
    mensPremiumShortSleeveTShirts: buildVistaDesignProduct(
        'mensPremiumShortSleeveTShirts',
        'District® Soft-Touch Unisex T-shirt',
        ['en-US']
    ),
    elevateMensDadeShortSleevePoloShirtNa: buildVistaDesignProduct(
        'elevateMensDadeShortSleevePoloShirtNa',
        'Elevate Dade Polo Shirt',
        ['en-US']
    ),
    gildanDryBlendJerseySportShirtNa: buildVistaDesignProduct(
        'gildanDryBlendJerseySportShirtNa',
        'Gildan® DryBlend™Jersey Sport Shirt',
        ['en-US']
    ),
    premiumWomensShortSleeveTShirts: buildVistaDesignProduct(
        'premiumWomensShortSleeveTShirts',
        "District® Soft-Touch Women's T-shirt",
        ['en-US']
    ),
    gildanPerformanceLongSleeveShirtNa: buildVistaDesignProduct(
        'gildanPerformanceLongSleeveShirtNa',
        'Gildan® Performance Long Sleeve Shirt',
        ['en-US']
    ),
    yupoongFivePanelClassicTruckerCapNa: buildVistaDesignProduct(
        'yupoongFivePanelClassicTruckerCapNa',
        'Yupoong Five-Panel Classic Trucker Cap',
        ['en-US']
    ),
    longSleeveMensTShirt: buildVistaDesignProduct(
        'longSleeveMensTShirt',
        'Fruit of the Loom® Long Sleeve T-shirt',
        ['en-US']
    ),
    gildanHeavyCottonWomensShortSleeveTShirtNa: buildVistaDesignProduct(
        'gildanHeavyCottonWomensShortSleeveTShirtNa',
        "Gildan® Heavy Cotton™ Women's Short Sleeve T-Shirt",
        ['en-US']
    ),
    districtVeryImportantTeeVNeckWithFullFrontPrintNa: buildVistaDesignProduct(
        'districtVeryImportantTeeVNeckWithFullFrontPrintNa',
        'District® Very Important Tee® V-Neck with Full-Front Print and Back Print',
        ['en-US']
    ),
    jerzeesWomensPoloShirts: buildVistaDesignProduct(
        'jerzeesWomensPoloShirts',
        'JERZEES® Piqué Women’s Polo Shirt',
        ['en-US']
    ),
    gildanDryBlendLongSleeveTShirtNa: buildVistaDesignProduct(
        'gildanDryBlendLongSleeveTShirtNa',
        'Gildan® DryBlend™ Long Sleeve T-Shirt',
        ['en-US']
    ),
    gildanHeavyBlendAdultHoodedSweatshirtNa: buildVistaDesignProduct(
        'gildanHeavyBlendAdultHoodedSweatshirtNa',
        'Gildan® Heavy Blend™ Adult Hooded Sweatshirt',
        ['en-US']
    ),
    elevateMensTazaKnitQuarterZipNa: buildVistaDesignProduct(
        'elevateMensTazaKnitQuarterZipNa',
        'Elevate™ Taza Knit Quarter-Zip Jacket',
        ['en-US']
    ),
    carharttRainDefenderPaxtonHeavyweightHoodedSweatshirtNa: buildVistaDesignProduct(
        'carharttRainDefenderPaxtonHeavyweightHoodedSweatshirtNa',
        'Carhartt® Rain Defender® Paxton Heavyweight Hoodie',
        ['en-US']
    ),
    portCompanyMensCoreCottonInkPrintedTShirtNa: buildVistaDesignProduct(
        'portCompanyMensCoreCottonInkPrintedTShirtNa',
        "Port & Company® Men's Core Cotton Ink Printed T-Shirt",
        ['en-US']
    ),
    portAuthorityRibKnitCuffBeanie: buildVistaDesignProduct(
        'portAuthorityRibKnitCuffBeanie',
        'Port Authority® Rib Knit Cuff Beanie',
        ['en-US']
    ),
    featherLiteValuePolyesterSportShirtNa: buildVistaDesignProduct(
        'featherLiteValuePolyesterSportShirtNa',
        'FeatherLite® Value Polyester Sport Shirt',
        ['en-US']
    ),
    anvil45OunceRingSpunInkPrintedLightweightMensTShirtNa: buildVistaDesignProduct(
        'anvil45OunceRingSpunInkPrintedLightweightMensTShirtNa',
        'Anvil By Gildan Softstyle® Lightweight T-Shirt',
        ['en-US']
    ),
    portAuthorityLadiesSummitFleeceFullZipJacketNa: buildVistaDesignProduct(
        'portAuthorityLadiesSummitFleeceFullZipJacketNa',
        'Port Authority® Ladies Summit Fleece Full-Zip Jacket',
        ['en-US']
    ),
    legacyDashboardTruckerCapNa: buildVistaDesignProduct(
        'legacyDashboardTruckerCapNa',
        'LEGACY Dashboard Trucker Cap',
        ['en-US']
    ),
    districtWomensVeryImportantTeeLongSleeveWithFullFrontAndBackScreenprintNa:
        buildVistaDesignProduct(
            'districtWomensVeryImportantTeeLongSleeveWithFullFrontAndBackScreenprintNa',
            "District® Women's Very Important Tee® Long Sleeve with Full-Front and Back Screenprint",
            ['en-US']
        ),
    comfortColorsGarmentDyedHeavyweightRingspunLongSleeveTShirtNa: buildVistaDesignProduct(
        'comfortColorsGarmentDyedHeavyweightRingspunLongSleeveTShirtNa',
        'Comfort Colors™ Garment Dyed Heavyweight Ringspun Long Sleeve T-Shirt',
        ['en-US']
    ),
    independentTradingCoUnisexSpecialBlendRaglanHoodedFullZipSweatshirtNa: buildVistaDesignProduct(
        'independentTradingCoUnisexSpecialBlendRaglanHoodedFullZipSweatshirtNa',
        'Independent Trading Co. Unisex Special Blend Raglan Hooded Full-Zip Sweatshirt',
        ['en-US']
    ),
    districtVitMuscleTankWithLeftChestAndBackScreenprintNa: buildVistaDesignProduct(
        'districtVitMuscleTankWithLeftChestAndBackScreenprintNa',
        'District® V.I.T.™ Muscle Tank with Left Chest and Back Screenprint',
        ['en-US']
    ),
    sportTekSleevelessPerformanceTeeScreenprintNa: buildVistaDesignProduct(
        'sportTekSleevelessPerformanceTeeScreenprintNa',
        'Sport-Tek® Sleeveless Performance Tee Screenprint',
        ['en-US']
    ),
    core365MensFusionChromaSoftPiquePoloNa: buildVistaDesignProduct(
        'core365MensFusionChromaSoftPiquePoloNa',
        'Core365® Fusion ChromaSoft™ Piqué Polo Shirt',
        ['en-US']
    ),
    districtWomensVeryImportantTeeWithFullFrontScreenprintNa: buildVistaDesignProduct(
        'districtWomensVeryImportantTeeWithFullFrontScreenprintNa',
        "District® Women's Very Important Tee® with Full-Front Screenprint",
        ['en-US']
    ),
    districtWomensVeryImportantTeeVNeckWithLeftChestPrintNa: buildVistaDesignProduct(
        'districtWomensVeryImportantTeeVNeckWithLeftChestPrintNa',
        'District® Women’s Very Important Tee® V-Neck with Left Chest Print',
        ['en-US']
    ),
    jerzeesNuBlendYouthHoodedSweatshirtNa: buildVistaDesignProduct(
        'jerzeesNuBlendYouthHoodedSweatshirtNa',
        'JERZEES® NuBlend® Youth Hooded Sweatshirt',
        ['en-US']
    ),
    portAuthorityTorrentWaterproofJacketNa: buildVistaDesignProduct(
        'portAuthorityTorrentWaterproofJacketNa',
        'Port Authority® Torrent Waterproof Jacket',
        ['en-US']
    ),
    allmadeWomensTriBlendVNeckTeeNa: buildVistaDesignProduct(
        'allmadeWomensTriBlendVNeckTeeNa',
        "Allmade Women's Tri-Blend V-neck Tee",
        ['en-US']
    ),
    alternativeCottonJerseyGoToTeeNa: buildVistaDesignProduct(
        'alternativeCottonJerseyGoToTeeNa',
        'Alternative® Cotton Jersey Go-To Tee',
        ['en-US']
    ),
    districtVeryImportantTeeLongSleeveWithFullFrontScreenprintNa: buildVistaDesignProduct(
        'districtVeryImportantTeeLongSleeveWithFullFrontScreenprintNa',
        'District® Very Important Tee® Long Sleeve with Full-Front Screenprint',
        ['en-US']
    ),
    driDuckTrekVestNa: buildVistaDesignProduct('driDuckTrekVestNa', 'DRI-Duck® Trek Vest', [
        'en-US',
    ]),
    sportTekYouthSportWickHoodedPulloverNa: buildVistaDesignProduct(
        'sportTekYouthSportWickHoodedPulloverNa',
        'Sport-Tek® Youth Sport-Wick® Hooded Pullover',
        ['en-US']
    ),
    spyderPassageBeanieNa: buildVistaDesignProduct(
        'spyderPassageBeanieNa',
        'Spyder® Passage Beanie',
        ['en-US']
    ),
    portAuthorityMicrofleeceVestNa: buildVistaDesignProduct(
        'portAuthorityMicrofleeceVestNa',
        'Port Authority® Microfleece Vest',
        ['en-US']
    ),
    pumaMensGtCrossfadePoloShirtNa: buildVistaDesignProduct(
        'pumaMensGtCrossfadePoloShirtNa',
        "PUMA® Men's GT Crossfade Polo Shirt",
        ['en-US']
    ),
    portAuthorityLadiesDiamondHeatherFleeceFullZipJacketNa: buildVistaDesignProduct(
        'portAuthorityLadiesDiamondHeatherFleeceFullZipJacketNa',
        'Port Authority ® Ladies Diamond Heather Fleece Full-Zip Jacket',
        ['en-US']
    ),
    sportTekSportWickFlexFleece14ZipNa: buildVistaDesignProduct(
        'sportTekSportWickFlexFleece14ZipNa',
        'Sport-Tek® Sport-Wick® Flex Fleece 1/4-Zip',
        ['en-US']
    ),
    northEndLadiesRadarQuarterZipPerformanceLongSleeveTopNa: buildVistaDesignProduct(
        'northEndLadiesRadarQuarterZipPerformanceLongSleeveTopNa',
        "North End® Ladies' Radar Quarter-Zip Performance Long-Sleeve Top",
        ['en-US']
    ),
    districtMadeWomensPerfectTriLongSleeveHoodieNa: buildVistaDesignProduct(
        'districtMadeWomensPerfectTriLongSleeveHoodieNa',
        "District Made® Women's Perfect Tri® Long Sleeve Hoodie",
        ['en-US']
    ),
    roots73MensRiversideFullZipHoodieNa: buildVistaDesignProduct(
        'roots73MensRiversideFullZipHoodieNa',
        'Roots73® Riverside Full-Zip Hoodie',
        ['en-US']
    ),
    portCompanySweatshirtToteNa: buildVistaDesignProduct(
        'portCompanySweatshirtToteNa',
        'Port & Company Sweatshirt Tote',
        ['en-US']
    ),
    wickedMensWovenShirtNa: buildVistaDesignProduct(
        'wickedMensWovenShirtNa',
        'Wicked Men’s Woven® Shirt',
        ['en-US']
    ),
    womensEasyCareGinghamCheckShirtNa: buildVistaDesignProduct(
        'womensEasyCareGinghamCheckShirtNa',
        'Women’s Easy-Care Gingham Check Shirt',
        ['en-US']
    ),
    portCompanyBouncerPocketTeeNa: buildVistaDesignProduct(
        'portCompanyBouncerPocketTeeNa',
        'Port & Company® Bouncer Pocket Tee',
        ['en-US']
    ),
    eddieBauerLadiesFleeceVestNa: buildVistaDesignProduct(
        'eddieBauerLadiesFleeceVestNa',
        'Eddie Bauer® Ladies Fleece Vest',
        ['en-US']
    ),
    columbiaMensBahamaIiLongSleeveShirtNa: buildVistaDesignProduct(
        'columbiaMensBahamaIiLongSleeveShirtNa',
        'Columbia® Bahama™ II Dress Shirt',
        ['en-US']
    ),
    driDuckMensFieldLongSleeveShirtNa: buildVistaDesignProduct(
        'driDuckMensFieldLongSleeveShirtNa',
        "DRI-Duck® Men's Field Long Sleeve Shirt",
        ['en-US']
    ),
    jerzeesNuBlendYouthCrewneckSweatshirtNa: buildVistaDesignProduct(
        'jerzeesNuBlendYouthCrewneckSweatshirtNa',
        'JERZEES® NuBlend® Youth Crewneck Sweatshirt',
        ['en-US']
    ),
    sportTekWomensRashguardTeeNa: buildVistaDesignProduct(
        'sportTekWomensRashguardTeeNa',
        "Sport-Tek® Women's Rashguard Tee",
        ['en-US']
    ),
    sportTekColorblockMicropiqueSportWickPoloNa: buildVistaDesignProduct(
        'sportTekColorblockMicropiqueSportWickPoloNa',
        'Sport-Tek® Colorblock Micropique Sport-Wick® Polo',
        ['en-US']
    ),
    portAuthorityLadiesCoreClassicPiquePoloNa: buildVistaDesignProduct(
        'portAuthorityLadiesCoreClassicPiquePoloNa',
        'Port Authority® Ladies Core Classic Pique Polo',
        ['en-US']
    ),
    portAuthorityRTekStretchFleeceHeadbandNa: buildVistaDesignProduct(
        'portAuthorityRTekStretchFleeceHeadbandNa',
        'Port Authority® R-Tek® Stretch Fleece Headband',
        ['en-US']
    ),
    sportTekWomensTriumphCowlNeckPulloverNa: buildVistaDesignProduct(
        'sportTekWomensTriumphCowlNeckPulloverNa',
        "Sport-Tek® Women's Triumph Cowl Neck Pullover",
        ['en-US']
    ),
    portAuthoritySuperProOxfordShirtNa: buildVistaDesignProduct(
        'portAuthoritySuperProOxfordShirtNa',
        'Port Authority® SuperPro™ Oxford Shirt',
        ['en-US']
    ),
    portAuthorityProCamouflageSeriesGarmentWashedCapNa: buildVistaDesignProduct(
        'portAuthorityProCamouflageSeriesGarmentWashedCapNa',
        'Port Authority® Pro Camouflage Series Garment-Washed Cap',
        ['en-US']
    ),
    callawayTonalWomensPoloShirtNa: buildVistaDesignProduct(
        'callawayTonalWomensPoloShirtNa',
        "Callaway® Tonal Women's Polo Shirt",
        ['en-US']
    ),
    portCompanyLadiesLongSleeveFanFavoriteVNeckTee: buildVistaDesignProduct(
        'portCompanyLadiesLongSleeveFanFavoriteVNeckTee',
        'Port & Company® Fan Favorite™ Women’s Long Sleeve T-shirt',
        ['en-US']
    ),
    nikeGolfDriFitSwooshPerforatedCapNa: buildVistaDesignProduct(
        'nikeGolfDriFitSwooshPerforatedCapNa',
        'Nike Golf - Dri-FIT® Swoosh Perforated Cap',
        ['en-US']
    ),
    portAuthorityLadiesDryZoneOttomanPoloNa: buildVistaDesignProduct(
        'portAuthorityLadiesDryZoneOttomanPoloNa',
        "Port Authority® Ladies' Dry Zone® Ottoman Polo",
        ['en-US']
    ),
    districtWomensVeryImportantTeeLongSleeveVNeckWithLeftChestAndBackPrintNa:
        buildVistaDesignProduct(
            'districtWomensVeryImportantTeeLongSleeveVNeckWithLeftChestAndBackPrintNa',
            "District® Women's Very Important Tee® Long Sleeve V-Neck with Left Chest and Back Print",
            ['en-US']
        ),
    sportsmanKnitScarfNa: buildVistaDesignProduct('sportsmanKnitScarfNa', 'Sportsman Knit Scarf', [
        'en-US',
    ]),
    '2GolfBallsTeesInTubeNa': buildVistaDesignProduct(
        '2GolfBallsTeesInTubeNa',
        '2 Golf Balls & Tees in Tube',
        ['en-US']
    ),
    underArmourMensUltimateTeamJacketNa: buildVistaDesignProduct(
        'underArmourMensUltimateTeamJacketNa',
        "Under Armour® Men's Ultimate Team Jacket",
        ['en-US']
    ),
    stormCreekLadiesLightweightMicrofleece14ZipPulloverNa: buildVistaDesignProduct(
        'stormCreekLadiesLightweightMicrofleece14ZipPulloverNa',
        "Storm Creek® Ladies' Lightweight Microfleece 1/4-Zip Pullover",
        ['en-US']
    ),
    sportTekWomensDraftHoodieTankNa: buildVistaDesignProduct(
        'sportTekWomensDraftHoodieTankNa',
        "Sport-Tek® Women's Draft Hoodie Tank",
        ['en-US']
    ),
    core365WomensCruiseTwoLayerFleeceBondedSoftShellVestNa: buildVistaDesignProduct(
        'core365WomensCruiseTwoLayerFleeceBondedSoftShellVestNa',
        "Core365® Women's Cruise Two-Layer Fleece Bonded Soft Shell Vest",
        ['en-US']
    ),
    theNorthFaceApexBarrierSoftShellJacketNa: buildVistaDesignProduct(
        'theNorthFaceApexBarrierSoftShellJacketNa',
        'The North Face® Apex Barrier Soft Shell Jacket',
        ['en-US']
    ),
    portAuthorityLadiesMicrofleeceJacketNa: buildVistaDesignProduct(
        'portAuthorityLadiesMicrofleeceJacketNa',
        "Port Authority® Ladies' Microfleece Jacket",
        ['en-US']
    ),
    portAuthorityRTekStretchAntiPillFleeceBeanieCapNa: buildVistaDesignProduct(
        'portAuthorityRTekStretchAntiPillFleeceBeanieCapNa',
        'Carhartt® Force Helmet-Liner Mask',
        ['en-US']
    ),
    core365WomensBalanceColorblockPerformancePiquePoloNa: buildVistaDesignProduct(
        'core365WomensBalanceColorblockPerformancePiquePoloNa',
        "Core365® Women's Balance Colorblock Performance Pique Polo",
        ['en-US']
    ),
    portCompanyYouthPulloverHoodedSweatshirtNa: buildVistaDesignProduct(
        'portCompanyYouthPulloverHoodedSweatshirtNa',
        'Port & Company® Youth Pullover Hooded Sweatshirt',
        ['en-US']
    ),
    portCompanyCamoBeanieCapNa: buildVistaDesignProduct(
        'portCompanyCamoBeanieCapNa',
        'Port & Company® Camo Beanie Cap',
        ['en-US']
    ),
    riversEndLadiesPerformanceEdgeShortSleevePoloNa: buildVistaDesignProduct(
        'riversEndLadiesPerformanceEdgeShortSleevePoloNa',
        "River's End® Ladies' Performance 'Edge' Short Sleeve Polo",
        ['en-US']
    ),
    nikeGolfLadiesTechBasicDriFitPoloNa: buildVistaDesignProduct(
        'nikeGolfLadiesTechBasicDriFitPoloNa',
        'Nike® Golf Ladies Tech Basic Dri-FIT Polo',
        ['en-US']
    ),
    golfTeesBallMarkerSetNa: buildVistaDesignProduct(
        'golfTeesBallMarkerSetNa',
        'Golf Tees & Ball Marker Set',
        ['en-US']
    ),
    fullCustomPerformanceTankTop: buildVistaDesignProduct(
        'fullCustomPerformanceTankTop',
        'Full Custom Performance Tank Top',
        ['en-US']
    ),
    portAuthorityMensPerformanceFineJacquardPoloNa: buildVistaDesignProduct(
        'portAuthorityMensPerformanceFineJacquardPoloNa',
        'Port Authority® Performance Fine Jacquard Polo Shirt',
        ['en-US']
    ),
    theNorthFaceLadiesRidgelineSoftShellVestNa: buildVistaDesignProduct(
        'theNorthFaceLadiesRidgelineSoftShellVestNa',
        'The North Face® Ridgeline Women’s Soft Shell Vest',
        ['en-US']
    ),
    portAuthorityReversibleChargerVestNa: buildVistaDesignProduct(
        'portAuthorityReversibleChargerVestNa',
        'Port Authority® Reversible Charger Vest',
        ['en-US']
    ),
    womensHudsonDenimShirtNa: buildVistaDesignProduct(
        'womensHudsonDenimShirtNa',
        "Women's Hudson Denim Shirt",
        ['en-US']
    ),
    personalizedMugs: buildVistaDesignProduct('personalizedMugs', 'Custom Mugs', ['en-US']),
    bookmarks: buildVistaDesignProduct('bookmarks', 'Bookmarks', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    carDoorDecals: buildVistaDesignProduct('carDoorDecals', 'Car Door Decals', [
        'en-IE',
        'en-US',
        'fr-FR',
        'nl-NL',
    ]),
    businessCardMagnets: buildVistaDesignProduct('businessCardMagnets', 'Magnetic Business Cards', [
        'en-US',
    ]),
    refrigeratorMagnets: buildVistaDesignProduct('refrigeratorMagnets', 'Fridge Magnets', [
        'en-US',
    ]),
    customTickets: buildVistaDesignProduct('customTickets', 'Custom Tickets', [
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'en-US',
        'fi-FI',
        'fr-FR',
        'nl-BE',
        'nl-NL',
    ]),
    windowClings: buildVistaDesignProduct('windowClings', 'Window Clings', [
        'en-US',
        'fi-FI',
        'it-CH',
    ]),
    canvasPrints: buildVistaDesignProduct('canvasPrints', 'Canvas Prints', ['en-US']),
    carWindowDecals: buildVistaDesignProduct('carWindowDecals', 'Car Window Decals', [
        'de-AT',
        'de-CH',
        'en-US',
        'nl-BE',
    ]),
    tensionFabricDisplays: buildVistaDesignProduct(
        'tensionFabricDisplays',
        'Tension Fabric Displays',
        ['en-US']
    ),
    wallDecals: buildVistaDesignProduct('wallDecals', 'Wall Decals', ['en-US']),
    popUpDisplays: buildVistaDesignProduct('popUpDisplays', 'Pop-Up Displays', ['en-US']),
    naNapkins: buildVistaDesignProduct('naNapkins', 'Custom Napkins', ['en-US']),
    postcardMagnets: buildVistaDesignProduct('postcardMagnets', 'Magnetic Postcards', ['en-US']),
    naAdultBirthdayInvitations: buildVistaDesignProduct(
        'naAdultBirthdayInvitations',
        'Adult Birthday Invitations',
        ['en-US']
    ),
    windowPerforatedVinyl: buildVistaDesignProduct(
        'windowPerforatedVinyl',
        'Perforated Window Vinyl',
        ['en-US']
    ),
    fleeceBlankets: buildVistaDesignProduct('fleeceBlankets', 'Custom Blankets', ['en-US']),
    graduationInvitations: buildVistaDesignProduct(
        'graduationInvitations',
        'Graduation Invitations & Announcements',
        ['en-US']
    ),
    framedPrints: buildVistaDesignProduct('framedPrints', 'Framed Prints', ['en-US']),
    flagsFabricOnly: buildVistaDesignProduct('flagsFabricOnly', 'Flags - Fabric Only', ['en-US']),
    customLicensePlates: buildVistaDesignProduct('customLicensePlates', 'Custom License Plates', [
        'en-US',
    ]),
    kidsBirthdayInvitations: buildVistaDesignProduct(
        'kidsBirthdayInvitations',
        "Kids' Birthday Invitations",
        ['en-US']
    ),
    popUpDisplaysGraphicOnly: buildVistaDesignProduct(
        'popUpDisplaysGraphicOnly',
        'Pop-Up Displays – Graphic Only',
        ['en-US']
    ),
    pillows: buildVistaDesignProduct('pillows', 'Custom Pillows', ['en-US']),
    babyShowerInvitations: buildVistaDesignProduct(
        'babyShowerInvitations',
        'Baby Shower Invitations',
        ['en-US']
    ),
    canCoolers: buildVistaDesignProduct('canCoolers', 'Printed Can Cooler', ['en-US']),
    placemats: buildVistaDesignProduct('placemats', 'Placemats', [
        'de-CH',
        'en-US',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
    ]),
    saveTheDateMagnets: buildVistaDesignProduct('saveTheDateMagnets', 'Save the Date Magnets', [
        'en-US',
    ]),
    floorStandups: buildVistaDesignProduct('floorStandups', 'Floor Standups', ['en-US']),
    stainlessSteelWideMouthInsulatedBottle22Oz: buildVistaDesignProduct(
        'stainlessSteelWideMouthInsulatedBottle22Oz',
        'Stainless Steel Wide-Mouth Insulated Bottle – 22 oz.',
        ['en-US']
    ),
    floorDecals: buildVistaDesignProduct('floorDecals', 'Floor Decals', ['en-US']),
    photoCoasters: buildVistaDesignProduct('photoCoasters', 'Custom Coasters', ['en-US']),
    laserEtchedStemlessWineGlass: buildVistaDesignProduct(
        'laserEtchedStemlessWineGlass',
        'Etched Stemless Wine Glasses',
        ['en-US']
    ),
    babyAnnouncements: buildVistaDesignProduct('babyAnnouncements', 'Baby Announcements', [
        'en-US',
    ]),
    engravedPintGlass: buildVistaDesignProduct('engravedPintGlass', 'Etched Pint Glasses', [
        'en-US',
    ]),
    customCanvasBags: buildVistaDesignProduct('customCanvasBags', 'Custom Tote Bags', ['en-US']),
    engravedPenWithMetallicFinishBestForSimpleLogosNa: buildVistaDesignProduct(
        'engravedPenWithMetallicFinishBestForSimpleLogosNa',
        'Engraved Pen with Metallic Finish - best for simple logos',
        ['en-US']
    ),
    naMilestoneBirthdayInvitations: buildVistaDesignProduct(
        'naMilestoneBirthdayInvitations',
        'Teen Birthday Invitations',
        ['en-US']
    ),
    naPhotoBalloons: buildVistaDesignProduct('naPhotoBalloons', 'Photo Balloons', ['en-US']),
    kidsWaterBottle: buildVistaDesignProduct('kidsWaterBottle', "Kids' Water Bottle", ['en-US']),
    '16OzCustomPrintedTumblers': buildVistaDesignProduct(
        '16OzCustomPrintedTumblers',
        'Double-Wall Tumbler with Straw – 16 oz.',
        ['en-US']
    ),
    aluminumWaterBottleWithCarabiner26Oz: buildVistaDesignProduct(
        'aluminumWaterBottleWithCarabiner26Oz',
        'Aluminum Water Bottle with Carabiner – 26 oz.',
        ['en-US']
    ),
    laserEtchedCanGlass: buildVistaDesignProduct('laserEtchedCanGlass', 'Etched Can Glasses', [
        'en-US',
    ]),
    jivePenNa: buildVistaDesignProduct('jivePenNa', 'Jive Pen', ['en-US']),
    naCottonDrawstringBags: buildVistaDesignProduct(
        'naCottonDrawstringBags',
        'VistaPrint® Cotton Drawstring Bag',
        ['en-US']
    ),
    '26OzStainlessSteelWaterBottleNa': buildVistaDesignProduct(
        '26OzStainlessSteelWaterBottleNa',
        '26 oz. Stainless Steel Water Bottle',
        ['en-US']
    ),
    woodPrints: buildVistaDesignProduct('woodPrints', 'Wood Prints', ['en-US']),
    urbanDrawstringSportspackNa: buildVistaDesignProduct(
        'urbanDrawstringSportspackNa',
        'Urban Drawstring Sportspack',
        ['en-US']
    ),
    '22OzStainlessSteelTravelMugFullColorNa': buildVistaDesignProduct(
        '22OzStainlessSteelTravelMugFullColorNa',
        'Stainless Steel Vacuum Insulated Tumbler – Set of 36',
        ['en-US']
    ),
    bicClicSticWhiteBarrelBlackInkNa: buildVistaDesignProduct(
        'bicClicSticWhiteBarrelBlackInkNa',
        'BIC® Clic Stic : White barrel Black ink',
        ['en-US']
    ),
    koozieBasicCollapsibleBottleKoolerNa: buildVistaDesignProduct(
        'koozieBasicCollapsibleBottleKoolerNa',
        'Koozie® Basic Collapsible Bottle Cooler',
        ['en-US']
    ),
    britebrandClassicBallpointPen: buildVistaDesignProduct(
        'britebrandClassicBallpointPen',
        'Design Wrap Squiggle Pen',
        ['en-US']
    ),
    portAuthorityUltraCoreCinchPackNa: buildVistaDesignProduct(
        'portAuthorityUltraCoreCinchPackNa',
        'Port Authority® Ultra-Core Cinch Pack',
        ['en-US']
    ),
    naReligiousBabyEventInvitations: buildVistaDesignProduct(
        'naReligiousBabyEventInvitations',
        'Religious Baby Event Invitations',
        ['en-US']
    ),
    koozieZipUpBottleKoolerNa: buildVistaDesignProduct(
        'koozieZipUpBottleKoolerNa',
        'Koozie® Zip Up Bottle Cooler',
        ['en-US']
    ),
    mmsSilverTinsWithPersonalizedLid15OzNa: buildVistaDesignProduct(
        'mmsSilverTinsWithPersonalizedLid15OzNa',
        "M&M'S® Silver Tins with Personalized Lid- 1.5 oz.",
        ['en-US']
    ),
    swell40OzThermalTravelerNa: buildVistaDesignProduct(
        'swell40OzThermalTravelerNa',
        "S'well 40 Oz Thermal Traveler",
        ['en-US']
    ),
    towelsByTmgCompetitorFitnessTowelNa: buildVistaDesignProduct(
        'towelsByTmgCompetitorFitnessTowelNa',
        'Towels by TMG Competitor Fitness Towel',
        ['en-US']
    ),
    towelsByTmgRivieraBeachTowelNa: buildVistaDesignProduct(
        'towelsByTmgRivieraBeachTowelNa',
        'Riviera Beach Towel',
        ['en-US']
    ),
    domedTinWithHouseShapedMintsCaseOf125Na: buildVistaDesignProduct(
        'domedTinWithHouseShapedMintsCaseOf125Na',
        'Domed Tin with House Shaped Mints',
        ['en-US']
    ),
    oppoBluetoothHeadphonesWithMicNa: buildVistaDesignProduct(
        'oppoBluetoothHeadphonesWithMicNa',
        'Oppo Bluetooth® Headphones with Mic',
        ['en-US']
    ),
    coltonCopperVacuumInsulatedBottle20ozNa: buildVistaDesignProduct(
        'coltonCopperVacuumInsulatedBottle20ozNa',
        'Colton Copper Vacuum Insulated Bottle – 20 oz.',
        ['en-US']
    ),
    ultraSlimSoftyWithStylusBlackInkNa: buildVistaDesignProduct(
        'ultraSlimSoftyWithStylusBlackInkNa',
        'Ultra Slim Softy with Stylus Black Ink',
        ['en-US']
    ),
    microCoralPlushBlanketNa: buildVistaDesignProduct(
        'microCoralPlushBlanketNa',
        'Micro Coral Plush Blanket',
        ['en-US']
    ),
    velocityFlashDrive4GBNa: buildVistaDesignProduct(
        'velocityFlashDrive4GBNa',
        'Velocity Flash Drive 4GB',
        ['en-US']
    ),
    pillowBoxes: buildVistaDesignProduct('pillowBoxes', 'Pillow Boxes', ['en-US', 'es-US']),
    h2goLodgeThermalBottle169Oz: buildVistaDesignProduct(
        'h2goLodgeThermalBottle169Oz',
        'h2go® Lodge Thermal Bottle – 16.9 oz.',
        ['en-US']
    ),
    h2goVentureThermalBottle40Oz: buildVistaDesignProduct(
        'h2goVentureThermalBottle40Oz',
        'h2go® Venture Thermal Bottle – 40 oz.',
        ['en-US']
    ),
    portAuthorityTheOriginalPackNa: buildVistaDesignProduct(
        'portAuthorityTheOriginalPackNa',
        'Port Authority® The Original Pack',
        ['en-US']
    ),
    individuallyWrappedFlavorBurstCrystalFruitCandiesCaseOf1000Na: buildVistaDesignProduct(
        'individuallyWrappedFlavorBurstCrystalFruitCandiesCaseOf1000Na',
        'Individually Wrapped FlavorBurst® Crystal Fruit Candies Case of 1000',
        ['en-US']
    ),
    fullColorMugWithChocolateButtonsMugDrop11OzCaseOf36Na: buildVistaDesignProduct(
        'fullColorMugWithChocolateButtonsMugDrop11OzCaseOf36Na',
        'Full-Color Mug with Chocolate Buttons Mug Drop – 11 oz. - Case of 24',
        ['en-US']
    ),
    assortedJellyBelly1ozGoodyBagNa: buildVistaDesignProduct(
        'assortedJellyBelly1ozGoodyBagNa',
        'Assorted Jelly Belly® 1oz. Goody Bag',
        ['en-US']
    ),
    largeLaserEngravedBambooCuttingBoardWithSiliconeGripNa: buildVistaDesignProduct(
        'largeLaserEngravedBambooCuttingBoardWithSiliconeGripNa',
        'Large Laser Engraved Bamboo Cutting Board with Silicone Grip',
        ['en-US']
    ),
    reposeRecycledCottonDrawstringBagNa: buildVistaDesignProduct(
        'reposeRecycledCottonDrawstringBagNa',
        'Repose Recycled Cotton Drawstring Bag',
        ['en-US']
    ),
    zigooLaserEngravedSiliconeCollapsibleBottle18OzNa: buildVistaDesignProduct(
        'zigooLaserEngravedSiliconeCollapsibleBottle18OzNa',
        'Zigoo Laser Engraved Silicone Collapsible Bottle – 18 oz.',
        ['en-US']
    ),
    sharpieSGelWhiteBarrelPenNa: buildVistaDesignProduct(
        'sharpieSGelWhiteBarrelPenNa',
        'Sharpie S-Gel White Barrel Pen',
        ['en-US']
    ),
    gripWriteAmBlackInkPenWithAntimicrobialAdditiveNa: buildVistaDesignProduct(
        'gripWriteAmBlackInkPenWithAntimicrobialAdditiveNa',
        'Grip Write AM Black Ink Pen with Antimicrobial Additive',
        ['en-US']
    ),
    wpPorterUtensilSet: buildVistaDesignProduct('wpPorterUtensilSet', 'W&P Porter Utensil Set', [
        'en-US',
    ]),
    rectangleMultichargerNa: buildVistaDesignProduct(
        'rectangleMultichargerNa',
        'Rectangle Multicharger',
        ['en-US']
    ),
    '27OzH2goElevateEastmanTritanBottleNa': buildVistaDesignProduct(
        '27OzH2goElevateEastmanTritanBottleNa',
        '27 oz h2go Elevate Eastman Tritan Bottle',
        ['en-US']
    ),
    manchesterPenWithBlueInkDigitalFullColorWrapNa: buildVistaDesignProduct(
        'manchesterPenWithBlueInkDigitalFullColorWrapNa',
        'Manchester Pen: Sleek design vibrant full-color wrap.',
        ['en-US']
    ),
    manchesterPenWithBlackInkDigitalFullColorWrapNa: buildVistaDesignProduct(
        'manchesterPenWithBlackInkDigitalFullColorWrapNa',
        'Manchester Pen: personalized elegance in black ink.',
        ['en-US']
    ),
    executive15ComputerBackpackNa: buildVistaDesignProduct(
        'executive15ComputerBackpackNa',
        'Executive 15 Computer Backpack',
        ['en-US']
    ),
    portAuthorityCamouflageColorblockSportDuffelNa: buildVistaDesignProduct(
        'portAuthorityCamouflageColorblockSportDuffelNa',
        'Port Authority® Camouflage Colorblock Sport Duffel',
        ['en-US']
    ),
    championDrawstringCinchBackpackNa: buildVistaDesignProduct(
        'championDrawstringCinchBackpackNa',
        'Champion Drawstring Cinch Backpack',
        ['en-US']
    ),
    highSierraMagnumCompuBackpackNa: buildVistaDesignProduct(
        'highSierraMagnumCompuBackpackNa',
        'High Sierra® Magnum Compu-Backpack',
        ['en-US']
    ),
    theCapitol20DuffelNa: buildVistaDesignProduct('theCapitol20DuffelNa', 'The Capitol 20 Duffel', [
        'en-US',
    ]),
    '16OzAvaPlasticStadiumCupNa': buildVistaDesignProduct(
        '16OzAvaPlasticStadiumCupNa',
        '16 oz Ava Plastic Stadium Cup',
        ['en-US']
    ),
    losAngelesPenWithBlackInkDigitalFullColorWrapNa: buildVistaDesignProduct(
        'losAngelesPenWithBlackInkDigitalFullColorWrapNa',
        'Los Angeles Pen with Black Ink Digital Full Color Wrap',
        ['en-US']
    ),
    engravedBottleOpenerKeychainNa: buildVistaDesignProduct(
        'engravedBottleOpenerKeychainNa',
        'Engraved Bottle Opener Keychain',
        ['en-US']
    ),
    vaultRfidSecurity15ComputerBackpackNa: buildVistaDesignProduct(
        'vaultRfidSecurity15ComputerBackpackNa',
        'Vault RFID Security 15 Computer Backpack',
        ['en-US']
    ),
    lilShorty17OunceAluminumSportsBottleNa: buildVistaDesignProduct(
        'lilShorty17OunceAluminumSportsBottleNa',
        "Lil' Shorty Aluminum Sports Water Bottle – 17 oz.",
        ['en-US']
    ),
    aftRecycledDuffelNa: buildVistaDesignProduct('aftRecycledDuffelNa', 'AFT Recycled Duffel', [
        'en-US',
    ]),
    goGoMobilePhoneStandScreenCleanerNa: buildVistaDesignProduct(
        'goGoMobilePhoneStandScreenCleanerNa',
        'Go Go Mobile Phone Stand-Screen Cleaner',
        ['en-US']
    ),
    engineerMultiToolNa: buildVistaDesignProduct('engineerMultiToolNa', 'Engineer Multi-Tool', [
        'en-US',
    ]),
    brewCopperVacuumInsulatedLaserEngravedMug12OzNa: buildVistaDesignProduct(
        'brewCopperVacuumInsulatedLaserEngravedMug12OzNa',
        'Brew Copper Vacuum Insulated Laser Engraved Mug – 12 oz.',
        ['en-US']
    ),
    softTouchEvelynStylusPenWithBlackInkNa: buildVistaDesignProduct(
        'softTouchEvelynStylusPenWithBlackInkNa',
        'Soft Touch Evelyn Stylus Pen with Black Ink',
        ['en-US']
    ),
    caseLogicEra14ComputerAttacheNa: buildVistaDesignProduct(
        'caseLogicEra14ComputerAttacheNa',
        'Case Logic® Era 14 Computer Attache',
        ['en-US']
    ),
    avianaPalmerDoubleWallBottle17OzNa: buildVistaDesignProduct(
        'avianaPalmerDoubleWallBottle17OzNa',
        'Aviana™ Palmer Double Wall Bottle – 17 oz.',
        ['en-US']
    ),
    rubberBandBallInCaseNa: buildVistaDesignProduct(
        'rubberBandBallInCaseNa',
        'Rubber Band Ball in Case',
        ['en-US']
    ),
    gourmetCubeWithCandyCornNa: buildVistaDesignProduct(
        'gourmetCubeWithCandyCornNa',
        'Gourmet Cube with Candy Corn',
        ['en-US']
    ),
    flashPowerBankNa: buildVistaDesignProduct('flashPowerBankNa', 'Flash Power Bank', ['en-US']),
    towelsByTmgRivieraWhiteBeachTowelNa: buildVistaDesignProduct(
        'towelsByTmgRivieraWhiteBeachTowelNa',
        'Towels by TMG Riviera White Beach Towel',
        ['en-US']
    ),
    redBarrelPaperMateWriteBrothersStickPenNa: buildVistaDesignProduct(
        'redBarrelPaperMateWriteBrothersStickPenNa',
        'Red Barrel PaperMate® Write Brothers Stick Pen',
        ['en-US']
    ),
    signityRollerballSoftyBlueInkPenLaserEngravedNa: buildVistaDesignProduct(
        'signityRollerballSoftyBlueInkPenLaserEngravedNa',
        'Signity Rollerball Softy Blue Ink Pen - Laser Engraved',
        ['en-US']
    ),
    souvenirHewPenNa: buildVistaDesignProduct('souvenirHewPenNa', 'Souvenir® Hew Pen', ['en-US']),
    souvenierTfwPenNa: buildVistaDesignProduct('souvenierTfwPenNa', 'Souvenier™ TFW Pen', [
        'en-US',
    ]),
    blackMarbleAndWoodLaserEngravedCoasterSetNa: buildVistaDesignProduct(
        'blackMarbleAndWoodLaserEngravedCoasterSetNa',
        'Black Marble and Wood Laser Engraved Coaster Set',
        ['en-US']
    ),
    cullenMultiFunctionTapeMeasureNa: buildVistaDesignProduct(
        'cullenMultiFunctionTapeMeasureNa',
        'Cullen Multi-Function Tape Measure',
        ['en-US']
    ),
    goodValueDoubleLayerGolfTowelNa: buildVistaDesignProduct(
        'goodValueDoubleLayerGolfTowelNa',
        'Good Value® Double Layer Golf Towel',
        ['en-US']
    ),
    theHipsterDeluxeFannyPackNa: buildVistaDesignProduct(
        'theHipsterDeluxeFannyPackNa',
        'The Hipster Deluxe Fanny Pack',
        ['en-US']
    ),
    mikeIkesSmallTopViewTinNa: buildVistaDesignProduct(
        'mikeIkesSmallTopViewTinNa',
        "Mike & Ike's® Small Top View Tin",
        ['en-US']
    ),
    highSierraFlyByCompuBackpackNa: buildVistaDesignProduct(
        'highSierraFlyByCompuBackpackNa',
        'High Sierra® Fly-By Compu-Backpack',
        ['en-US']
    ),
    bigFoot20OzStainlessSteelVacuumTumblerNa: buildVistaDesignProduct(
        'bigFoot20OzStainlessSteelVacuumTumblerNa',
        'Perfect Line Stainless Steel Tumbler – 20 oz.',
        ['en-US']
    ),
    samsoniteAndante222WheeledDuffelNa: buildVistaDesignProduct(
        'samsoniteAndante222WheeledDuffelNa',
        'Samsonite® Andante Wheeled Duffel Bag',
        ['en-US']
    ),
    arcticZoneRepreveBackpackCoolerWithSlingNa: buildVistaDesignProduct(
        'arcticZoneRepreveBackpackCoolerWithSlingNa',
        'Arctic Zone® Repreve® Backpack Cooler with Sling',
        ['en-US']
    ),
    roundOrnamentNa: buildVistaDesignProduct('roundOrnamentNa', 'Round Ornament', ['en-US']),
    harritonAdultRestore49OzScrubBottomNa: buildVistaDesignProduct(
        'harritonAdultRestore49OzScrubBottomNa',
        'Harriton Adult Restore 4.9 oz. Scrub Bottom',
        ['en-US']
    ),
    skyLightStylusPenWithBlackInkNa: buildVistaDesignProduct(
        'skyLightStylusPenWithBlackInkNa',
        'Sky Light Stylus Pen with Black Ink',
        ['en-US']
    ),
    stainlessSteelTravelMug22OzNa: buildVistaDesignProduct(
        'stainlessSteelTravelMug22OzNa',
        'Stainless Steel Travel Mug – 22 oz.',
        ['en-US']
    ),
    andrewsJournalWithFullColorInkjetNa: buildVistaDesignProduct(
        'andrewsJournalWithFullColorInkjetNa',
        'Andrews Journal with Full-Color Inkjet',
        ['en-US']
    ),
    bikeWaterBottle20OzNa: buildVistaDesignProduct(
        'bikeWaterBottle20OzNa',
        'Bike Design Wrap Sports Water Bottle – 20 oz.',
        ['en-US']
    ),
    ecoFriendlyBambooArlingtonPenWithBlueInkNa: buildVistaDesignProduct(
        'ecoFriendlyBambooArlingtonPenWithBlueInkNa',
        'Eco-Friendly Bamboo Arlington Pen with Blue Ink',
        ['en-US']
    ),
    sportTekWindPantScreenprintNa: buildVistaDesignProduct(
        'sportTekWindPantScreenprintNa',
        'Sport-Tek® Wind Pant Screenprint',
        ['en-US']
    ),
    safeTravelsTraverseWalletNa: buildVistaDesignProduct(
        'safeTravelsTraverseWalletNa',
        'Safe Travels Traverse Wallet',
        ['en-US']
    ),
    highSierraImpactBackpackNa: buildVistaDesignProduct(
        'highSierraImpactBackpackNa',
        'High Sierra® Impact Backpack',
        ['en-US']
    ),
    fullColorMineralHughesGelStylusPenWithBlueInkNa: buildVistaDesignProduct(
        'fullColorMineralHughesGelStylusPenWithBlueInkNa',
        'Full-Color Mineral Hughes Gel Stylus Pen with Blue Ink',
        ['en-US']
    ),
    theTotalPackageToolSetNa: buildVistaDesignProduct(
        'theTotalPackageToolSetNa',
        'The Total Package Tool Set',
        ['en-US']
    ),
    executiveAlphaPenGiftSetWithBlueInkNa: buildVistaDesignProduct(
        'executiveAlphaPenGiftSetWithBlueInkNa',
        'Executive Alpha Pen Gift Set with Blue Ink',
        ['en-US']
    ),
    camelBakInsulatedBottleWithCupNa: buildVistaDesignProduct(
        'camelBakInsulatedBottleWithCupNa',
        'CamelBak® Insulated Bottle with Cup',
        ['en-US']
    ),
    arcticZoneTitanThermalHpCopperLaserEngravedTumbler30OzNa: buildVistaDesignProduct(
        'arcticZoneTitanThermalHpCopperLaserEngravedTumbler30OzNa',
        'Arctic Zone® Titan Thermal HP® Copper Laser Engraved Tumbler – 30 oz.',
        ['en-US']
    ),
    goodValueTransparentDrySack25LNa: buildVistaDesignProduct(
        'goodValueTransparentDrySack25LNa',
        'Good Value® Transparent Dry Sack – 2.5L',
        ['en-US']
    ),
    premiumFullColorPrintedBags: buildVistaDesignProduct(
        'premiumFullColorPrintedBags',
        'Premium Full Color Paper Bag Design',
        ['en-US', 'es-US']
    ),
    customRibbon: buildVistaDesignProduct('customRibbon', 'Custom Ribbon Design', [
        'en-US',
        'es-US',
    ]),
    customPizzaBoxes: buildVistaDesignProduct('customPizzaBoxes', 'Custom Pizza Box Design', [
        'en-US',
        'es-US',
        'it-CH',
    ]),
    customGableBox: buildVistaDesignProduct('customGableBox', 'Gable Box Design', [
        'en-US',
        'es-US',
    ]),
    foldedHangtags: buildVistaDesignProduct('foldedHangtags', 'Folded Hang Tag Design', [
        'en-US',
        'es-US',
    ]),
    customCoffeeSleeves: buildVistaDesignProduct(
        'customCoffeeSleeves',
        'Custom Coffee Sleeve Design',
        ['en-US', 'es-US']
    ),
    soapBoxes: buildVistaDesignProduct('soapBoxes', 'Soap Box Design', ['en-US', 'es-US']),
    squareMailer: buildVistaDesignProduct('squareMailer', 'Square Mailer Box Design', [
        'en-US',
        'es-US',
    ]),
    luxuryGiftBags: buildVistaDesignProduct('luxuryGiftBags', 'Luxury Gift Bag Design', [
        'en-US',
        'es-US',
    ]),
    autoLockBottomBoxes: buildVistaDesignProduct(
        'autoLockBottomBoxes',
        'Auto Lock Bottom Box Design',
        ['en-US', 'es-US']
    ),
    polytape: buildVistaDesignProduct('polytape', 'Self-Adhesive Packaging Tape Design', [
        'en-US',
        'es-US',
    ]),
    chocolateBarBoxes: buildVistaDesignProduct('chocolateBarBoxes', 'Chocolate Bar Box Design', [
        'en-US',
        'es-US',
    ]),
    tuckTopMailerBoxes: buildVistaDesignProduct(
        'tuckTopMailerBoxes',
        'Tuck Top Mailer Box Design',
        ['en-US', 'es-US']
    ),
    plasticCups: buildVistaDesignProduct('plasticCups', 'Colored Plastic Cup Design', [
        'en-US',
        'es-US',
    ]),
    dieCutHandleBags: buildVistaDesignProduct('dieCutHandleBags', 'Die-Cut Handle Bag Design', [
        'en-US',
        'es-US',
    ]),
    envelopeSeals: buildVistaDesignProduct('envelopeSeals', 'Envelope Seal Design', [
        'da-DK',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'en-US',
        'es-US',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'it-IT',
        'nl-BE',
        'nl-NL',
        'sv-SE',
    ]),
    mailingLabels: buildVistaDesignProduct('mailingLabels', 'Mailing Label Design', [
        'en-SG',
        'en-US',
        'es-US',
    ]),
    wineBags: buildVistaDesignProduct('wineBags', 'Wine Bag Design', ['en-US', 'es-US']),
    treatBags: buildVistaDesignProduct('treatBags', 'Treat Bag Design', ['en-US', 'es-US']),
    flyersandLeaflets: buildVistaDesignProduct('flyersandLeaflets', 'Flyers Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    customStickers: buildVistaDesignProduct('customStickers', 'Custom Stickers Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    mensTee: buildVistaDesignProduct('mensTee', 'Bella + Canvas® Jersey T-shirt Design', [
        'en-AU',
        'en-NZ',
    ]),
    spotUVBusinessCards: buildVistaDesignProduct(
        'spotUVBusinessCards',
        'Spot UV Business Cards Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-AU',
            'en-NZ',
            'en-SG',
            'es-ES',
            'fi-FI',
            'fr-CH',
            'fr-FR',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    magneticBusinessCards: buildVistaDesignProduct(
        'magneticBusinessCards',
        'Magnetic Business Cards Design',
        ['en-AU', 'en-SG']
    ),
    foilBusinessCards: buildVistaDesignProduct('foilBusinessCards', 'Foil Business Cards Design', [
        'en-AU',
    ]),
    entryLevelBusinessCards: buildVistaDesignProduct(
        'entryLevelBusinessCards',
        'Entry Level Business Cards Design',
        ['en-AU', 'en-SG']
    ),
    mailingBoxes: buildVistaDesignProduct('mailingBoxes', 'Mailing Boxes Design', [
        'en-AU',
        'en-NZ',
    ]),
    swingTags: buildVistaDesignProduct('swingTags', 'Swing Tags Design', [
        'en-AU',
        'en-NZ',
        'en-SG',
    ]),
    customPrintedTissuePaper: buildVistaDesignProduct(
        'customPrintedTissuePaper',
        'Custom Tissue Paper Design',
        ['en-AU']
    ),
    promotionalBoxes: buildVistaDesignProduct('promotionalBoxes', 'Product Boxes Design', [
        'en-AU',
    ]),
    paperCups: buildVistaDesignProduct('paperCups', 'Paper Cups Design', ['en-AU']),
    paperBag: buildVistaDesignProduct('paperBag', 'Paper Bags Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-NZ',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'sv-SE',
    ]),
    foldedLeaflets: buildVistaDesignProduct('foldedLeaflets', 'Brochures Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),

    magneticPostcards: buildVistaDesignProduct('magneticPostcards', 'Magnetic Postcards Design', [
        'en-AU',
        'it-CH',
    ]),
    doorHanger: buildVistaDesignProduct('doorHanger', 'Door Hangers Design', [
        'da-DK',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-SG',
        'nb-NO',
        'nl-BE',
        'sv-SE',
    ]),
    lawnSigns: buildVistaDesignProduct('lawnSigns', 'Corflute Signs Design', [
        'en-AU',
        'en-GB',
        'fi-FI',
        'fr-FR',
        'it-CH',
        'it-IT',
        'sv-SE',
    ]),
    aFrameSandwichBoards: buildVistaDesignProduct(
        'aFrameSandwichBoards',
        'Outdoor A-Frames Design',
        ['en-AU']
    ),
    giftCertificates: buildVistaDesignProduct('giftCertificates', 'Gift Certificates Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'en-SG',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    hardcoverNotebooks: buildVistaDesignProduct(
        'hardcoverNotebooks',
        'Hardcover Notebooks Design',
        ['en-AU']
    ),
    fabricStamps: buildVistaDesignProduct('fabricStamps', 'Clothing Stamps Design', ['en-AU']),
    outdoorRetractableBanner: buildVistaDesignProduct(
        'outdoorRetractableBanner',
        'Double Sided Pull Up Banners Design',
        ['en-AU', 'en-NZ']
    ),
    waterproofSheetStickers: buildVistaDesignProduct(
        'waterproofSheetStickers',
        'Waterproof Sheet Stickers Design',
        ['en-AU', 'en-NZ', 'en-SG']
    ),
    syntheticRollLabels: buildVistaDesignProduct(
        'syntheticRollLabels',
        'Waterproof Roll Labels Design',
        ['en-AU']
    ),
    transparentLabels: buildVistaDesignProduct('transparentLabels', 'Clear Roll Labels Design', [
        'en-AU',
    ]),
    womensTee: buildVistaDesignProduct(
        'womensTee',
        'Bella + Canvas® Women’s Slim-Fit T-shirt Design',
        ['en-AU']
    ),
    gildanMensPoloShirt: buildVistaDesignProduct(
        'gildanMensPoloShirt',
        "Gildan® Men's Polo Shirt Design Design",
        ['en-AU', 'en-NZ', 'en-SG']
    ),
    gildanWomensPoloShirt: buildVistaDesignProduct(
        'gildanWomensPoloShirt',
        "Gildan® Women's Polo Shirt Design Design",
        ['en-AU', 'en-NZ', 'en-SG']
    ),
    printedCap: buildVistaDesignProduct('printedCap', 'VistaPrint® Universal Fit Cap Design', [
        'en-AU',
    ]),
    basicKidsTShirts: buildVistaDesignProduct('basicKidsTShirts', 'Gildan® Kids’ T-shirt Design', [
        'en-AU',
    ]),
    enclosureCards: buildVistaDesignProduct('enclosureCards', 'Wedding Enclosure Cards Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-AU',
        'en-GB',
        'en-IE',
        'en-NZ',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    receptionCards: buildVistaDesignProduct('receptionCards', 'Wedding Reception Cards Design', [
        'en-AU',
    ]),
    stubbyHolder: buildVistaDesignProduct('stubbyHolder', 'Personalised Stubby Holder Design', [
        'en-AU',
    ]),
    paperCoasters: buildVistaDesignProduct('paperCoasters', 'Paper Coasters Design', ['en-AU']),
    circleStamps: buildVistaDesignProduct('circleStamps', 'Round Self Inking Stamps Design', [
        'da-DK',
        'de-AT',
        'de-DE',
        'en-GB',
        'en-IE',
        'en-NZ',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    nameTags: buildVistaDesignProduct('nameTags', 'Name Tags Design', [
        'de-CH',
        'en-IE',
        'en-NZ',
        'en-SG',
        'fi-FI',
        'nl-BE',
    ]),
    r231fResultLadiesPrintableSoftshellJacket: buildVistaDesignProduct(
        'r231fResultLadiesPrintableSoftshellJacket',
        "Result® Women's Softshell Jacket Design",
        ['en-NZ']
    ),
    saveTheDateMagnet: buildVistaDesignProduct(
        'saveTheDateMagnet',
        'Save the Date Magnets Design',
        [
            'da-DK',
            'de-AT',
            'de-DE',
            'en-GB',
            'en-IE',
            'en-NZ',
            'es-ES',
            'fi-FI',
            'fr-BE',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    rsvpCards: buildVistaDesignProduct('rsvpCards', 'RSVP Cards Design', [
        'da-DK',
        'de-AT',
        'de-DE',
        'en-GB',
        'en-IE',
        'en-NZ',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    flagsNZ: buildVistaDesignProduct('flagsNZ', 'Flags Design', ['en-NZ']),
    hoodie: buildVistaDesignProduct('hoodie', 'Zip Up Hoodies Design', ['en-NZ']),
    fruitOfTheLoomBasicTShirt: buildVistaDesignProduct(
        'fruitOfTheLoomBasicTShirt',
        "Basic S/S Men's T-Shirts Design",
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-GB',
            'en-IE',
            'es-ES',
            'fi-FI',
            'fr-BE',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    loyaltyCards: buildVistaDesignProduct('loyaltyCards', 'Loyalty Cards Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    gbbPremiumBusinessCards: buildVistaDesignProduct(
        'gbbPremiumBusinessCards',
        'GBB Premium Business Cards Design',
        ['da-DK', 'en-IE']
    ),
    cottoVerMensTShirt: buildVistaDesignProduct(
        'cottoVerMensTShirt',
        "Cottover Men's Short Sleeve Crew T-Shirts Design",
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-GB',
            'en-IE',
            'es-ES',
            'fi-FI',
            'fr-BE',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'sv-SE',
        ]
    ),
    multiDecoVistaprintEmbroideredHats: buildVistaDesignProduct(
        'multiDecoVistaprintEmbroideredHats',
        'Embroidered Caps Design',
        ['da-DK', 'it-CH', 'nb-NO', 'nl-NL']
    ),
    euRectangleStickers: buildVistaDesignProduct(
        'euRectangleStickers',
        'Rectangle Stickers Design',
        [
            'da-DK',
            'de-AT',
            'de-DE',
            'en-GB',
            'es-ES',
            'fr-BE',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nl-NL',
            'sv-SE',
        ]
    ),
    photoboothMagnets: buildVistaDesignProduct('photoboothMagnets', 'Photobooth Magnet Design', [
        'da-DK',
        'fr-BE',
        'fr-CH',
        'sv-SE',
    ]),
    ringBinders: buildVistaDesignProduct('ringBinders', 'Binders Design', [
        'da-DK',
        'pt-PT',
        'sv-SE',
    ]),
    fliplidboxes: buildVistaDesignProduct('fliplidboxes', 'Flip Lid Box Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    wrappingPaper: buildVistaDesignProduct('wrappingPaper', 'Wrapping Paper Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    pulloutBoxes: buildVistaDesignProduct('pulloutBoxes', 'Pull-out Boxes Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'pt-PT',
    ]),
    papercup: buildVistaDesignProduct('papercup', 'Paper Cups Design', [
        'da-DK',
        'de-DE',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'sv-SE',
    ]),
    mailerBoxes: buildVistaDesignProduct('mailerBoxes', 'Mailing Boxes Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    promotionalBoxesEu: buildVistaDesignProduct('promotionalBoxesEu', 'Promotional Boxes Design', [
        'da-DK',
        'de-AT',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
    ]),
    euVinylBanners: buildVistaDesignProduct('euVinylBanners', 'Vinyl Banners Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    outdoorPoster: buildVistaDesignProduct('outdoorPoster', 'Outdoor Poster Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    textileStamps: buildVistaDesignProduct('textileStamps', 'Textile Stamps Design', [
        'da-DK',
        'fr-CH',
        'it-IT',
        'pt-PT',
        'sv-SE',
    ]),
    loyaltyCardStamp: buildVistaDesignProduct('loyaltyCardStamp', 'Loyalty Card Stamps Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'sv-SE',
    ]),
    weddingMenuCards: buildVistaDesignProduct('weddingMenuCards', 'Dinner Menus Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    weddingFoamBoards: buildVistaDesignProduct(
        'weddingFoamBoards',
        'Foam Board Wedding Signs Design',
        [
            'da-DK',
            'de-AT',
            'de-CH',
            'de-DE',
            'en-GB',
            'en-IE',
            'es-ES',
            'fi-FI',
            'fr-BE',
            'fr-CH',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    metallicBusinessCards: buildVistaDesignProduct(
        'metallicBusinessCards',
        'Metallic Business Cards Design',
        ['de-AT', 'de-CH', 'de-DE', 'en-IE', 'pt-PT']
    ),
    softTouchBusinessCards: buildVistaDesignProduct(
        'softTouchBusinessCards',
        'Soft Touch Business card Design',
        ['de-AT', 'fr-BE', 'fr-FR', 'it-IT', 'nl-BE']
    ),
    embroideredPolos: buildVistaDesignProduct(
        'embroideredPolos',
        "Embroidered Polos (Men's) Design",
        [
            'de-AT',
            'de-CH',
            'de-DE',
            'en-GB',
            'en-IE',
            'es-ES',
            'fi-FI',
            'fr-BE',
            'fr-FR',
            'it-CH',
            'it-IT',
            'nb-NO',
            'nl-BE',
            'nl-NL',
            'pt-PT',
            'sv-SE',
        ]
    ),
    fullCustomCyclingJersey: buildVistaDesignProduct(
        'fullCustomCyclingJersey',
        "Men's Cycling Jerseys Design",
        ['de-AT', 'de-CH', 'fr-CH', 'pt-PT']
    ),
    fullCustomToteBag: buildVistaDesignProduct(
        'fullCustomToteBag',
        'Full Custom Tote Bags Design',
        ['de-AT']
    ),
    bcId003HoodedSweat: buildVistaDesignProduct(
        'bcId003HoodedSweat',
        'B&C™ ID.003 - Hooded Sweat Design',
        ['de-AT']
    ),
    foodLabels: buildVistaDesignProduct('foodLabels', 'Food Packaging Labels Design', [
        'de-AT',
        'de-CH',
        'fr-FR',
        'it-IT',
        'nb-NO',
    ]),
    qRcodestickers: buildVistaDesignProduct('qRcodestickers', 'QR Code Stickers Design', [
        'de-AT',
        'de-CH',
        'de-DE',
        'es-ES',
        'fr-CH',
    ]),
    coasters: buildVistaDesignProduct('coasters', 'Coasters Design', ['de-AT', 'en-IE']),
    businessEnvelopes: buildVistaDesignProduct('businessEnvelopes', 'Business Envelopes Design', [
        'de-AT',
        'de-CH',
        'de-DE',
        'fr-BE',
    ]),
    flexiblePackagingDoypacks: buildVistaDesignProduct(
        'flexiblePackagingDoypacks',
        'Doypacks (Flexible packaging) Design',
        ['de-CH', 'en-GB', 'fr-BE', 'fr-FR', 'nb-NO', 'nl-NL', 'pt-PT']
    ),
    raisedFoilSwingTags: buildVistaDesignProduct(
        'raisedFoilSwingTags',
        'Raised Foil Hang Tags Design',
        ['de-CH', 'de-DE', 'en-GB', 'it-CH', 'it-IT', 'nl-NL']
    ),
    invoicePads: buildVistaDesignProduct('invoicePads', 'Invoice Pads Design', [
        'de-CH',
        'en-GB',
        'it-CH',
        'nl-BE',
        'nl-NL',
    ]),
    euMeshBanners: buildVistaDesignProduct('euMeshBanners', 'Mesh Banners Design', [
        'da-DK',
        'de-AT',
        'de-CH',
        'de-DE',
        'en-GB',
        'en-IE',
        'es-ES',
        'fi-FI',
        'fr-BE',
        'fr-CH',
        'fr-FR',
        'it-CH',
        'it-IT',
        'nb-NO',
        'nl-BE',
        'nl-NL',
        'pt-PT',
        'sv-SE',
    ]),
    fastShippingBusinessCards: buildVistaDesignProduct(
        'fastShippingBusinessCards',
        'Fast shipping Business Cards Design',
        ['en-GB']
    ),
    cottoVerWomensTShirt: buildVistaDesignProduct(
        'cottoVerWomensTShirt',
        "Cottover Women's Short Sleeve Crew T-Shirts Design",
        ['en-GB', 'fr-FR', 'it-IT']
    ),
    basicKidsTShirtShortSleeve: buildVistaDesignProduct(
        'basicKidsTShirtShortSleeve',
        "Basic S/S Kids' T-Shirts Design",
        ['en-IE', 'fi-FI', 'fr-BE', 'fr-CH']
    ),
    footballShirt: buildVistaDesignProduct('footballShirt', "Men's Soccer Jerseys Design", [
        'en-IE',
        'es-ES',
        'fr-CH',
        'it-CH',
        'pt-PT',
    ]),
    recycledpolymailers: buildVistaDesignProduct(
        'recycledpolymailers',
        'Recycled Poly Mailers Design',
        ['en-IE']
    ),
    gildanMensTShirt: buildVistaDesignProduct('gildanMensTShirt', "Men's Basic T-Shirt Design", [
        'en-SG',
    ]),
    vistaprint6PanelBaseballHat: buildVistaDesignProduct(
        'vistaprint6PanelBaseballHat',
        'Embroidered Hat Design',
        ['en-SG']
    ),
    asColourWomensStapleTShirt: buildVistaDesignProduct(
        'asColourWomensStapleTShirt',
        "AS Colour Women's T-Shirts Design",
        ['en-SG']
    ),
    gildanWomensTShirt: buildVistaDesignProduct(
        'gildanWomensTShirt',
        "Women's Basic Short Sleeve T-Shirt Design",
        ['en-SG']
    ),
    vistaprintTwoToneZipCottonToteBag: buildVistaDesignProduct(
        'vistaprintTwoToneZipCottonToteBag',
        'Premium Zipped Tote Bags (and ANZ) Design',
        ['en-SG']
    ),
    premiumAsColorMensTShirts: buildVistaDesignProduct(
        'premiumAsColorMensTShirts',
        "AS Colour Men's T-Shirts Design",
        ['en-SG']
    ),
    foldedSwingTags: buildVistaDesignProduct('foldedSwingTags', 'Folded Swing Tags Design', [
        'en-SG',
    ]),
    cardboardSigns: buildVistaDesignProduct('cardboardSigns', 'Cardboard Signs Design', ['en-SG']),
    signatureStamps: buildVistaDesignProduct('signatureStamps', 'Signature Stamps Design', [
        'en-SG',
    ]),
    singlePocketFolders: buildVistaDesignProduct(
        'singlePocketFolders',
        'Presentation Folders - Single Pocket Design',
        ['es-ES', 'it-IT']
    ),
    pillowBoxesEu: buildVistaDesignProduct('pillowBoxesEu', 'Pillow Boxes Design', [
        'es-ES',
        'fr-CH',
        'it-CH',
        'nl-BE',
    ]),
    journals: buildVistaDesignProduct('journals', 'Diaries Design', ['es-ES', 'pt-PT']),
    basicMensLongSleeveTShirt: buildVistaDesignProduct(
        'basicMensLongSleeveTShirt',
        "Basic L/S Men's T-Shirts Design",
        ['fi-FI', 'pt-PT']
    ),
    pinBadges: buildVistaDesignProduct('pinBadges', 'Buttons Design', ['fi-FI']),
    iceCreamCup: buildVistaDesignProduct('iceCreamCup', 'Ice Cream Cups Design', [
        'fi-FI',
        'sv-SE',
    ]),
    greenLineStamps: buildVistaDesignProduct(
        'greenLineStamps',
        'Green Line self-inking Stamps Design',
        ['fi-FI', 'fr-BE', 'fr-FR', 'it-CH', 'nb-NO']
    ),
    kidsBasicTShirts: buildVistaDesignProduct('kidsBasicTShirts', 'Basic T-shirts - Kids Design', [
        'fr-BE',
    ]),
    kidsFootballShirts: buildVistaDesignProduct(
        'kidsFootballShirts',
        'Youth Soccer Jerseys Design',
        ['fr-CH', 'it-CH']
    ),
    consumerDrawstringBag: buildVistaDesignProduct(
        'consumerDrawstringBag',
        'Consumer Drawstring Bags Design',
        ['it-CH']
    ),
    pvcFoamBoard: buildVistaDesignProduct('pvcFoamBoard', 'PVC Foam Boards Design', [
        'it-IT',
        'nb-NO',
    ]),
    handStamps: buildVistaDesignProduct('handStamps', 'Round Self-inking Hand Stamps Design', [
        'nb-NO',
    ]),
    fruitOfTheLoomPremiumHoodedSweatJacket: buildVistaDesignProduct(
        'fruitOfTheLoomPremiumHoodedSweatJacket',
        'Fruit of the Loom® Premium Hooded Sweat Jacket Design',
        ['nl-BE']
    ),
    fruitOfTheLoomValueweightLongsleeveT: buildVistaDesignProduct(
        'fruitOfTheLoomValueweightLongsleeveT',
        'Fruit of the Loom® Valueweight Longsleeve T Design',
        ['nl-BE']
    ),
    fullCustomSportsBomberJacket: buildVistaDesignProduct(
        'fullCustomSportsBomberJacket',
        'Full Custom Bomber Jackets Design',
        ['nl-NL']
    ),
    spotUvPostcards: buildVistaDesignProduct('spotUvPostcards', 'Spot UV Postcards Design', [
        'pt-PT',
    ]),
    autoLockBoxes: buildVistaDesignProduct('autoLockBoxes', 'Auto Lock Boxes Design', ['pt-PT']),
    euFabricBanners: buildVistaDesignProduct('euFabricBanners', 'Fabric Banners Design', ['pt-PT']),
};
